import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import { processHtmlWithAssets } from "../common/htmlPreview";
import PublicDocumentViewer from "./PublicDocumentViewer";

const mockGetSyncAuthToken = jest.fn();

jest.mock("../../../config/runtimeConfig", () => ({
  getApiBaseUrl: () => "",
}));

jest.mock("../../../utils/getSyncAuthToken", () => ({
  getSyncAuthToken: () => mockGetSyncAuthToken(),
}));

jest.mock("../hooks/useMMDataBridge", () => ({
  useMMDataBridge: () => ({ current: null }),
}));

jest.mock("../common/htmlPreview", () => ({
  ...jest.requireActual("../common/htmlPreview"),
  processHtmlWithAssets: jest.fn((html: string) => html),
}));

jest.mock("react-markdown", () => ({
  __esModule: true,
  default: ({ children }: { children?: unknown }) => <>{children}</>,
}));

jest.mock("remark-gfm", () => ({
  __esModule: true,
  default: () => {},
}));

const processHtmlMock = processHtmlWithAssets as jest.MockedFunction<
  typeof processHtmlWithAssets
>;

const htmlResponse = (html: string) => ({
  ok: true,
  status: 200,
  headers: {
    get: (name: string) =>
      name === "Content-Disposition"
        ? 'attachment; filename="dashboard.html"'
        : name === "Content-Type"
          ? "text/html"
          : null,
  },
  blob: jest.fn().mockResolvedValue({
    text: jest.fn().mockResolvedValue(html),
  }),
});

describe("PublicDocumentViewer mmData injection", () => {
  const fetchMock = jest.fn();

  beforeEach(() => {
    fetchMock.mockReset();
    processHtmlMock.mockClear();
    processHtmlMock.mockImplementation((html) => html);
    mockGetSyncAuthToken.mockReturnValue("public-viewer-token");
    (global as typeof globalThis & { fetch: typeof fetch }).fetch = fetchMock;
    Object.defineProperty(URL, "createObjectURL", {
      configurable: true,
      value: jest.fn(() => "blob:preview"),
    });
    Object.defineProperty(URL, "revokeObjectURL", {
      configurable: true,
      value: jest.fn(),
    });
  });

  const renderViewer = () =>
    render(
      <MemoryRouter initialEntries={["/public/documents/workspace%2Fdoc-1"]}>
        <Routes>
          <Route
            path="/public/documents/*"
            element={<PublicDocumentViewer />}
          />
        </Routes>
      </MemoryRouter>,
    );

  it("processes self-contained HTML exactly once with an empty asset map", async () => {
    const html = "<html><body>Dashboard</body></html>";
    fetchMock.mockResolvedValueOnce(htmlResponse(html));
    fetchMock.mockResolvedValueOnce({
      ok: true,
      json: jest.fn().mockResolvedValue({ configured: false }),
    });

    renderViewer();

    await waitFor(() => expect(processHtmlMock).toHaveBeenCalledWith(html, {}));
    expect(processHtmlMock).toHaveBeenCalledTimes(1);
    expect(fetchMock).toHaveBeenCalledTimes(2);
  });

  it.each([
    {
      name: "empty",
      assetResult: {
        ok: true,
        json: jest.fn().mockResolvedValue({ assets: {} }),
      },
    },
    {
      name: "non-OK",
      assetResult: { ok: false, status: 503 },
    },
    {
      name: "failed",
      assetResult: new Error("asset lookup failed"),
    },
  ])(
    "processes linked HTML with an empty map when asset resolution is $name",
    async ({ assetResult }) => {
      const html = '<html><body><img src="chart.png"></body></html>';
      fetchMock.mockResolvedValueOnce(htmlResponse(html));
      fetchMock.mockResolvedValueOnce({
        ok: true,
        json: jest.fn().mockResolvedValue({ configured: false }),
      });
      if (assetResult instanceof Error) {
        fetchMock.mockRejectedValueOnce(assetResult);
      } else {
        fetchMock.mockResolvedValueOnce(assetResult);
      }

      renderViewer();

      await waitFor(() =>
        expect(processHtmlMock).toHaveBeenCalledWith(html, {}),
      );
      expect(processHtmlMock).toHaveBeenCalledTimes(1);
    },
  );

  it("processes linked HTML exactly once with resolved assets", async () => {
    const html = '<html><body><img src="chart.png"></body></html>';
    const assets = {
      "chart.png": {
        data_uri: "data:image/png;base64,AA==",
        content_type: "image/png",
      },
    };
    fetchMock
      .mockResolvedValueOnce(htmlResponse(html))
      .mockResolvedValueOnce({
        ok: true,
        json: jest.fn().mockResolvedValue({ configured: false }),
      })
      .mockResolvedValueOnce({
        ok: true,
        json: jest.fn().mockResolvedValue({ assets }),
      });

    renderViewer();

    await waitFor(() =>
      expect(processHtmlMock).toHaveBeenCalledWith(html, assets),
    );
    expect(processHtmlMock).toHaveBeenCalledTimes(1);
  });

  it("uses the content-host session URL for HTML when configured", async () => {
    fetchMock
      .mockResolvedValueOnce(htmlResponse("<html><body>unused</body></html>"))
      .mockResolvedValueOnce({
        ok: true,
        json: jest.fn().mockResolvedValue({
          configured: true,
          url: "https://sharedby.example.test/c/session/site/index.html",
          origin: "https://sharedby.example.test",
        }),
      });

    renderViewer();

    await waitFor(() =>
      expect(screen.getByTitle("dashboard.html").getAttribute("src")).toBe(
        "https://sharedby.example.test/c/session/site/index.html",
      ),
    );
    expect(processHtmlMock).not.toHaveBeenCalled();
  });
});
